import { useState } from "react";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { useRateLimit, formatTimeRemaining } from "@/hooks/use-rate-limit";
import { Mail, Phone, Globe, Loader2, Lock, Clock } from "lucide-react";
import { Helmet } from "@/components/Helmet";
import { AltchaWidget } from "@/components/AltchaWidget";

const CONTACT_RATE_LIMIT = { maxAttempts: 5, windowMs: 60 * 60 * 1000 };

const Contact = () => {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [altchaToken, setAltchaToken] = useState<string | null>(null);
  const contactRateLimit = useRateLimit({ key: "contact_form", ...CONTACT_RATE_LIMIT });

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!contactRateLimit.checkLimit()) {
      toast({ title: "Too many requests", description: `Please wait ${formatTimeRemaining(contactRateLimit.timeUntilReset)} before submitting again.`, variant: "destructive" });
      return;
    }
    setIsSubmitting(true);
    try {
      const formData = new FormData(e.currentTarget);
      const data = { name: formData.get("name") as string, email: formData.get("email") as string, website: formData.get("website") as string, message: formData.get("message") as string, altchaToken };
      if (!altchaToken) {
        toast({ title: "Please complete the verification.", variant: "destructive" });
        setIsSubmitting(false);
        return;
      }
      const { error } = await supabase.functions.invoke("send-contact-email", { body: data });
      if (error) {
        if (error.message?.includes("Too many requests") || error.message?.includes("429")) {
          toast({ title: "Too many requests", description: "Please wait before submitting again.", variant: "destructive" });
          return;
        }
        throw error;
      }
      contactRateLimit.recordAttempt();
      toast({ title: "Request Received!", description: "We'll get back to you within 24 hours." });
      (e.target as HTMLFormElement).reset();
      setAltchaToken(null);
    } catch {
      toast({ title: "Error", description: "Failed to submit. Please try again.", variant: "destructive" });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Helmet>
        <title>Contact Us | ShadowScan</title>
        <meta name="description" content="Get in touch with ShadowScan about ShadowScan Guard, Basic Hosting, monitored security, and plan guidance for your WordPress sites." />
      </Helmet>
      <Navigation />
      <main className="flex-grow pt-24">
        <section className="relative py-20 overflow-hidden">
          <div className="absolute inset-0 bg-grid-pattern opacity-20 -z-10" />
          <div className="absolute inset-0 bg-gradient-to-b from-primary/10 via-transparent to-transparent -z-10" />
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center animate-fade-in">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-primary/30 bg-primary/5 mb-6">
                <span className="font-mono text-sm text-primary">// GET_IN_TOUCH</span>
              </div>
              <h1 className="text-4xl md:text-5xl font-heading font-bold mb-6">
                Contact <span className="text-gradient">ShadowScan</span>
              </h1>
              <p className="text-lg text-muted-foreground">
                Have questions about ShadowScan Guard, Basic Hosting, Essential, or Premium? We're here to help.
              </p>
            </div>
          </div>
        </section>

        <section className="py-16">
          <div className="container mx-auto px-4">
            <div className="grid lg:grid-cols-2 gap-12 max-w-5xl mx-auto">
              <div className="cyber-card rounded-xl p-8 border-primary/30">
                <form onSubmit={handleSubmit} className="space-y-5">
                  <div>
                    <Label htmlFor="name" className="text-sm font-mono text-muted-foreground">NAME *</Label>
                    <Input id="name" name="name" placeholder="Your name" required className="mt-2 bg-secondary/50" />
                  </div>
                  <div>
                    <Label htmlFor="email" className="text-sm font-mono text-muted-foreground">EMAIL *</Label>
                    <Input id="email" name="email" type="email" placeholder="your@email.com" required className="mt-2 bg-secondary/50" />
                  </div>
                  <div>
                    <Label htmlFor="website" className="text-sm font-mono text-muted-foreground">WEBSITE *</Label>
                    <Input id="website" name="website" placeholder="https://yoursite.com" required className="mt-2 bg-secondary/50" />
                  </div>
                  <div>
                    <Label htmlFor="message" className="text-sm font-mono text-muted-foreground">MESSAGE</Label>
                    <Textarea id="message" name="message" placeholder="Tell us about your security concerns..." rows={4} className="mt-2 bg-secondary/50" />
                  </div>
                  <div className="rounded-md border border-border/50 p-3 bg-muted/30">
                    <p className="text-xs text-muted-foreground mb-2">Please verify to submit</p>
                    <AltchaWidget onToken={setAltchaToken} className="block" />
                  </div>
                  <Button type="submit" size="lg" className="w-full bg-accent hover:bg-accent/90 text-accent-foreground" disabled={isSubmitting || contactRateLimit.isLimited}>
                    {contactRateLimit.isLimited ? (
                      <><Clock className="mr-2 h-5 w-5" />Wait {formatTimeRemaining(contactRateLimit.timeUntilReset)}</>
                    ) : isSubmitting ? (
                      <Loader2 className="h-5 w-5 animate-spin" />
                    ) : (
                      <><Lock className="mr-2 h-5 w-5" />Send Message</>
                    )}
                  </Button>
                  <p className="text-xs text-muted-foreground text-center">
                    {contactRateLimit.isLimited ? "Rate limited: Please wait before submitting again." : "We'll respond within 24 hours"}
                  </p>
                </form>
              </div>

              <div className="space-y-8">
                <div>
                  <div className="font-mono text-sm text-primary mb-2">// GET_IN_TOUCH</div>
                  <h2 className="text-2xl font-heading font-bold mb-4">Contact Us</h2>
                  <p className="text-muted-foreground">Have questions? We're here to help.</p>
                </div>
                <div className="space-y-6">
                  {[
                    { icon: Mail, label: "Email", value: "support@shadowscan.com.au", href: "mailto:support@shadowscan.com.au" },
                    { icon: Phone, label: "Phone", value: "0480 465 208", href: "tel:0480465208" },
                    { icon: Globe, label: "Location", value: "Serving WordPress sites worldwide" },
                  ].map((item, i) => (
                    <div key={i} className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-primary/10 border border-primary/30 rounded-lg flex items-center justify-center">
                        <item.icon className="h-5 w-5 text-primary" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-sm">{item.label}</h3>
                        {item.href ? (
                          <a href={item.href} className="text-muted-foreground hover:text-primary text-sm">{item.value}</a>
                        ) : (
                          <p className="text-muted-foreground text-sm">{item.value}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default Contact;
